<script lang="ts">
	import { clickoutside } from '@svelte-put/clickoutside';
	import bookCoverImg from '@lib/images/placeholders/book-cover.webp?enhanced';
	import { afterNavigate, goto } from '$app/navigation';
	import { page } from '$app/stores';
	import Search from 'virtual:icons/ion/search';
	import RoundClose from 'virtual:icons/ic/round-close';
	import { fly, slide } from 'svelte/transition';
	import { toast } from 'svelte-sonner';
	import { PUBLIC_API_URL, PUBLIC_BRAND_CODE, PUBLIC_SITE_NAME } from '$env/static/public';
	import { createFloatingActions } from 'svelte-floating-ui';
	import { offset, shift, flip } from 'svelte-floating-ui/core';
	import { isExpandedSearch, searchInputEl, searchQuery } from '@lib/stores/header.store';
	import type { CategoryItem, SearchItem } from '@lib/types';
	import { tick } from 'svelte';

	export let isFocused = false;

	let isOpenSearchPanel = false,
		focusedIndex = -1,
		recentlyViewedItems: SearchItem[] = [];

	const { userToken } = $page.data;
	const queryParam = $page.url.searchParams.get('query');
	const searchableRoutes = [
		'/search',
		'/[username]',
		'/my-library',
		'/bundles',
		'/categories/[slug]'
	];

	$searchQuery = queryParam ?? '';

	async function searchBook(option: SearchMode = 'all') {
		let urlString = `${$page.url.origin}/search`;

		if (option !== 'all') {
			const isSameRoute = searchOptions.map((option) => option.mode).includes(option);

			if (isSameRoute || (isSameRoute && !$searchQuery)) urlString = $page.url.href;
		}

		const searchUrl = new URL(urlString);
		const currentUrl = $page.url;
		const backUrl = $page.url.searchParams.get('back_url');

		if (backUrl) currentUrl.searchParams.delete('back_url');

		const defaultParams = [
			{ label: 'page', value: String(1) },
			{ label: 'query', value: $searchQuery },
			{ label: 'sort_by', value: 'newest' },
			{ label: 'back_url', value: currentUrl.href }
		];

		for (const { label, value } of defaultParams) {
			if (value) searchUrl.searchParams.set(label, value);
			else searchUrl.searchParams.delete(label);
		}

		goto(searchUrl, { replaceState: true })
			.then(() => closeAndResetStates())
			.finally(() => $searchInputEl.blur());
	}

	async function getRecentlyViewedItems() {
		try {
			const request = await fetch(`${PUBLIC_API_URL}/${PUBLIC_BRAND_CODE}/history`, {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${userToken}`
				}
			});

			const response = await request.json();
			if (request.ok) {
				return response.data?.search_items ?? [];
			}
		} catch (error) {
			toast.error('Oops, terjadi kesalahan saat memuat data');
		}

		return [];
	}

	async function openRecentlyViewed() {
		if (userToken) {
			recentlyViewedItems = await getRecentlyViewedItems();
			if (recentlyViewedItems.length) isOpenSearchPanel = true;
		}
	}

	const [floatingRef, floatingContent] = createFloatingActions({
		strategy: 'absolute',
		placement: 'bottom',
		middleware: [flip(), offset(10), shift({ crossAxis: true, padding: 20 })]
	});

	function handleKeyDown(event: KeyboardEvent) {
		const items = document.querySelectorAll('.search-item') as NodeListOf<HTMLElement>;
		if (items.length === 0) return;

		if (event.key === 'ArrowDown') {
			event.preventDefault();
			focusedIndex = (focusedIndex + 1) % items.length;
			items[focusedIndex].focus();
		} else if (event.key === 'ArrowUp') {
			event.preventDefault();
			focusedIndex = (focusedIndex - 1 + items.length) % items.length;
			items[focusedIndex].focus();
		} else if (event.key === 'Enter') {
			items[focusedIndex]?.click();
		}
	}

	function closeAndResetStates() {
		isOpenSearchPanel = false;
		focusedIndex = -1;
		isFocused = false;

		setTimeout(() => ($isExpandedSearch = false), 300);
	}

	afterNavigate(({ to }) => {
		if (!to) return;

		isOpenSearchPanel = false;
		const toRoute = to.route.id;

		if (toRoute) {
			const isOnSearchableRoute = searchableRoutes.some((route) => toRoute.includes(route));
			const queryParam = $page.url.searchParams.get('query');

			if (queryParam && isOnSearchableRoute) {
				$searchQuery = queryParam ?? '';
			} else {
				$isExpandedSearch = false;
				$searchQuery = '';
			}
		}
	});

	async function handleSearchButton() {
		const isWidescreen = matchMedia('(min-width: 640px)').matches;

		if (!$isExpandedSearch && !isWidescreen) {
			$isExpandedSearch = true;
			$searchQuery = queryParam ?? '';

			await tick();
			requestAnimationFrame(() => $searchInputEl.focus());
			return;
		}

		searchBook();
	}

	const catchEscape = (event: KeyboardEvent) => event.key === 'Escape' && closeAndResetStates();

	type SearchMode = 'all' | 'writers' | 'publishers' | 'my-library' | 'bundles' | 'categories';

	let searchOptions: {
		label: string;
		mode: SearchMode;
	}[] = [{ label: PUBLIC_SITE_NAME, mode: 'all' }];

	const options: {
		label: string;
		mode: SearchMode;
	}[] = [
		{
			label: 'Perpustakaan Saya',
			mode: 'my-library'
		},
		{
			label: 'Penulis',
			mode: 'writers'
		},
		{
			label: 'Penerbit',
			mode: 'publishers'
		},
		{
			label: 'Bundle Premium',
			mode: 'bundles'
		},
		{
			label: 'Kategori',
			mode: 'categories'
		}
	];

	function submitSearch() {
		const currentPath = $page.url.pathname.split('/')[1];
		const searchMode = searchOptions.find((option) => option.mode.includes(currentPath))?.mode;

		searchBook(searchMode ?? 'all');
	}

	let isShowSearchOption = false;

	$: {
		const routes = searchableRoutes.filter((route) => route !== '/search');
		const routeId = $page.route.id;

		isShowSearchOption = routes.some((route) => routeId?.includes(route)) && isFocused;

		// Reset `searchOptions` if changes
		searchOptions = [{ label: PUBLIC_SITE_NAME, mode: 'all' }];

		const publisher = $page.data?.publisher as ProfileData;
		const writer = $page.data?.writer as ProfileData;
		const category = $page.data?.category as CategoryItem;

		// Add search options
		options.forEach((option) => {
			const isOnRoute = $page.url.pathname.includes(`/${option.mode}`);

			if ((publisher || writer) && ['publishers', 'writers'].includes(option.mode)) {
				option.label = publisher?.name ?? writer?.name;
			} else if (category && option.mode === 'categories') {
				option.label = category.name;
			}

			if (isOnRoute) searchOptions = [...searchOptions, option];
		});
	}
</script>

<svelte:window on:keydown={catchEscape} />

<div
	class="searchbar-wrapper relative flex items-center gap-2"
	use:clickoutside
	on:clickoutside={closeAndResetStates}
>
	<div class="relative w-full" on:keydown={handleKeyDown} role="presentation">
		<form
			use:floatingRef
			on:submit|preventDefault={submitSearch}
			method="POST"
			class="w-full"
			class:hidden={!$isExpandedSearch}
			class:pre-md:block={!$isExpandedSearch || $isExpandedSearch}
		>
			<input
				on:focus|stopPropagation={() => {
					isFocused = true;
					openRecentlyViewed();
				}}
				bind:this={$searchInputEl}
				type="search"
				name="query"
				id="query"
				class="input input-bordered w-full"
				placeholder="Cari buku atau paket..."
				bind:value={$searchQuery}
				autocomplete="off"
			/>
		</form>
		{#if isOpenSearchPanel || (isShowSearchOption && $searchQuery)}
			<div
				use:floatingContent
				transition:fly={{ y: -10, duration: 350 }}
				class="absolute w-full rounded-xl border bg-base-100 p-5 shadow-soft"
			>
				{#if $searchQuery && isShowSearchOption}
					<div class:mb-2={recentlyViewedItems.length} transition:slide={{ duration: 350 }}>
						{#each searchOptions as option}
							<button
								on:click={() => searchBook(option.mode)}
								data-option={option.mode}
								type="button"
								class="search-item w-full space-y-1 rounded-md p-2 text-left hover:bg-base-200/30 focus:bg-base-200/30 focus:outline-none"
							>
								<p class="truncate text-sm">Cari "{$searchQuery}"</p>
								<p class="truncate text-xs font-medium text-primary">di {option.label}</p>
							</button>
						{/each}
					</div>
				{/if}
				{#if recentlyViewedItems.length}
					<div>
						<p class="mb-2 border-b pb-2 text-sm font-medium">
							Terakhir Dilihat ({recentlyViewedItems.length})
						</p>
						<div class="max-h-44 overflow-y-auto">
							{#each recentlyViewedItems as book}
								<a
									href="/books/{book.slug}"
									class="search-item flex items-center gap-3 rounded-md p-3 text-xs hover:bg-base-200/30 focus:bg-base-200/30 focus:outline-none"
									tabindex={0}
								>
									{#if book.image_url}
										<img src={book.image_url} class="book-cover" alt={book.title} />
									{:else}
										<enhanced:img src={bookCoverImg} class="book-cover" alt={book.title} />
									{/if}
									<div>
										<p class="line-clamp-2 font-medium">{book.title}</p>
										<p class="mt-1 line-clamp-1 text-xs text-gray-400">
											{#if book.BookWriter.length}
												{book.BookWriter[0].name}
												{#if book.BookWriter.length > 1}
													(+{book.BookWriter.length - 1})
												{/if}
											{:else}
												Penulis tidak dikenal
											{/if}
										</p>
									</div>
								</a>
							{/each}
						</div>
					</div>
				{/if}
			</div>
		{/if}
	</div>

	<div class="flex items-center gap-2">
		<button
			on:click={() => ($isExpandedSearch ? searchBook() : handleSearchButton())}
			type="button"
			class="btn btn-circle"
			class:btn-primary={$isExpandedSearch}
			aria-label="Cari"
		>
			<Search class="text-xl" />
		</button>

		{#if $isExpandedSearch}
			<button on:click={closeAndResetStates} type="button" aria-label="Tutup">
				<RoundClose class="text-2xl" />
			</button>
		{/if}
	</div>
</div>

<style lang="postcss">
	.searchbar-wrapper {
		@apply w-full pre-md:max-w-80 xl:max-w-96;
	}

	.book-cover {
		@apply aspect-[131/194] max-w-12 overflow-hidden rounded-sm bg-gray-100;

		& > img {
			@apply w-full object-cover object-center;
		}
	}
</style>
